/**
 * Build Your Box - Bundle Builder
 * Handles variant selection, product bundling, and cart integration
 */

class BuildYourBox {
  constructor() {
    // DOM Elements
    this.variantItems = document.querySelectorAll('[data-variant-item]');
    this.productItems = document.querySelectorAll('[data-product-item]');
    this.bundleGrid = document.querySelector('[data-bundle-grid]');
    this.progressCurrent = document.querySelector('[data-progress-current]');
    this.progressTotal = document.querySelector('[data-progress-total]');
    this.progressFill = document.querySelector('[data-progress-fill]');
    this.atcButton = document.querySelector('[data-add-to-cart]');
    this.atcRemaining = document.querySelector('[data-atc-remaining]');
    this.purchaseOptions = document.querySelectorAll('[data-purchase-option]');
    this.frequencySelect = document.querySelector('[data-frequency-select]');
    
    // Build Your Pint Popup Elements
    this.pintPopup = document.querySelector('.build-your-pint-custom-popup');
    this.pintPopupCloseButtons = document.querySelectorAll('[data-popup-close]');
    this.addPintToBoxBtn = document.querySelector('[data-add-pint-to-box]');
    
    // State
    this.selectedVariant = null;
    this.packQuantity = 4;
    this.bundleProducts = [];
    this.productQuantities = {};
    this.selectedPurchaseType = 'onetime';
    this.selectedSellingPlanId = null;
    this.currentPintProduct = null;

    this.init();
  }

  /**
   * Initialize the bundle builder
   */
  init() {
    this.setupVariantSelection();
    this.setupProductSelection();
    this.setupPurchaseOptions();
    this.setupAddToCart();
    this.setupPintPopup();

    // Select first variant by default
    if (this.variantItems.length > 0) {
      this.selectVariant(this.variantItems[0]);
    }
  }

  /**
   * Setup Build Your Pint popup handlers
   */
  setupPintPopup() {
    if (!this.pintPopup) return;

    // Close popup when clicking any element with data-popup-close
    this.pintPopupCloseButtons.forEach(closeBtn => {
      closeBtn.addEventListener('click', () => {
        this.closePintPopup();
      });
    });

    // Add to Box button handler
    if (this.addPintToBoxBtn) {
      this.addPintToBoxBtn.addEventListener('click', () => {
        this.addPintToBundle();
      });
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.pintPopup.classList.contains('build-your-pint-custom-popup-active')) {
        this.closePintPopup();
      }
    });
  }

  /**
   * Reset popup checkboxes and color-change classes
   */
  resetPintPopup() {
    if (!this.pintPopup) return;

    // Get all checkboxes
    const checkboxes = this.pintPopup.querySelectorAll('input[type="checkbox"]');
    
    checkboxes.forEach(checkbox => {
      // Uncheck the checkbox
      checkbox.checked = false;
      
      // Find parent div with class "custom-checkbox" and remove "color-change" class
      const parentDiv = checkbox.closest('.custom-checkbox');
      if (parentDiv) {
        parentDiv.classList.remove('color-change');
      }
    });
  }

  /**
   * Open Build Your Pint popup
   */
  openPintPopup(productItem) {
    if (!this.pintPopup) return;

    this.currentPintProduct = productItem;
    
    // Reset all checkboxes and color-change classes
    this.resetPintPopup();
    
    // Open popup
    this.pintPopup.classList.add('build-your-pint-custom-popup-active');
  }

  /**
   * Close Build Your Pint popup
   */
  closePintPopup() {
    if (!this.pintPopup) return;
    
    // Reset all checkboxes and color-change classes
    this.resetPintPopup();
    
    // Close popup
    this.pintPopup.classList.remove('build-your-pint-custom-popup-active');
    this.currentPintProduct = null;
  }

  /**
   * Add Build Your Pint to bundle with selected flavors
   */
  addPintToBundle() {
    if (!this.currentPintProduct) return;

    // Get all checked checkboxes
    const checkedCheckboxes = this.pintPopup.querySelectorAll('input[type="checkbox"]:checked');
    
    if (checkedCheckboxes.length === 0) {
      alert('Please select at least one flavor');
      return;
    }

    // Get checkbox label text instead of IDs
    const selectedFlavors = [];
    checkedCheckboxes.forEach(checkbox => {
      // Find the label associated with this checkbox
      const label = this.pintPopup.querySelector(`label[for="${checkbox.id}"]`);
      if (label) {
        // Get the text content from the label
        const flavorText = label.textContent.trim();
        selectedFlavors.push(flavorText);
      }
    });

    // Create product object with flavors
    const product = {
      id: this.currentPintProduct.dataset.productId,
      handle: this.currentPintProduct.dataset.productHandle,
      title: this.currentPintProduct.dataset.productTitle,
      image: this.currentPintProduct.dataset.productImage,
      isPint: true,
      selectedFlavors: selectedFlavors
    };

    // Add to bundle
    this.bundleProducts.push(product);
    
    // Update quantity display
    const productId = this.currentPintProduct.dataset.productId;
    this.productQuantities[productId]++;
    const valueDisplay = this.currentPintProduct.querySelector('[data-quantity-value]');
    if (valueDisplay) {
      valueDisplay.textContent = this.productQuantities[productId];
    }

    // Update slot with pint product
    this.updateSlot(this.bundleProducts.length - 1, product);
    this.updateProgress();
    this.updateAllProductButtons();

    // Close popup and reset
    this.closePintPopup();
  }

  /**
   * Check if product is "Build Your Pint"
   */
  isBuildYourPintProduct(productItem) {
    const productTitle = productItem.dataset.productTitle;
    const productHandle = productItem.dataset.productHandle;
    
    // Check if product title or handle contains "build your pint"
    return productTitle.toLowerCase().includes('build your pint') || 
           productHandle.toLowerCase().includes('build-your-pint');
  }

  /**
   * Setup variant selection handlers
   */
  setupVariantSelection() {
    this.variantItems.forEach(item => {
      item.addEventListener('click', () => this.selectVariant(item));
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.selectVariant(item);
        }
      });
    });
  }

  /**
   * Select a variant and update pack quantity
   */
  selectVariant(item) {
    const variantId = item.dataset.variantId;
    
    if (this.selectedVariant === variantId) return;

    // Update UI
    this.variantItems.forEach(v => {
      v.classList.remove('selected');
      v.setAttribute('aria-pressed', 'false');
    });
    
    item.classList.add('selected');
    item.setAttribute('aria-pressed', 'true');

    // Update state
    this.selectedVariant = variantId;
    this.packQuantity = parseInt(item.dataset.packQuantity) || 4;

    // Update prices
    this.updatePrices(item);

    // Update progress total
    this.progressTotal.textContent = this.packQuantity;
    this.atcRemaining.textContent = this.packQuantity;

    // Reset bundle
    this.resetBundle();
    this.generateSlots();
  }

  /**
   * Setup product selection handlers
   */
  setupProductSelection() {
    this.productItems.forEach(item => {
      const productId = item.dataset.productId;
      this.productQuantities[productId] = 0;

      const decreaseBtn = item.querySelector('[data-quantity-decrease]');
      const increaseBtn = item.querySelector('[data-quantity-increase]');
      const valueDisplay = item.querySelector('[data-quantity-value]');

      decreaseBtn.addEventListener('click', () => {
        if (this.productQuantities[productId] > 0) {
          this.productQuantities[productId]--;
          valueDisplay.textContent = this.productQuantities[productId];
          this.removeProductFromBundle(item);
          this.updateProductButtons(item);
        }
      });

      increaseBtn.addEventListener('click', () => {
        if (this.bundleProducts.length < this.packQuantity) {
          // Check if this is "Build Your Pint" product
          if (this.isBuildYourPintProduct(item)) {
            this.openPintPopup(item);
          } else {
            this.productQuantities[productId]++;
            valueDisplay.textContent = this.productQuantities[productId];
            this.addProductToBundle(item);
            this.updateProductButtons(item);
          }
        }
      });
    });
  }

  /**
   * Add product to bundle
   */
  addProductToBundle(item) {
    const product = {
      id: item.dataset.productId,
      handle: item.dataset.productHandle,
      title: item.dataset.productTitle,
      image: item.dataset.productImage,
      isPint: false
    };

    this.bundleProducts.push(product);
    this.updateSlot(this.bundleProducts.length - 1, product);
    this.updateProgress();
    this.updateAllProductButtons();
  }

  /**
   * Remove product from bundle
   */
  removeProductFromBundle(item) {
    const productId = item.dataset.productId;
    const index = this.bundleProducts.findIndex(p => p.id === productId);

    if (index !== -1) {
      this.bundleProducts.splice(index, 1);
      this.regenerateSlots();
      this.updateProgress();
      this.updateAllProductButtons();
    }
  }

  /**
   * Generate empty slots
   */
  generateSlots() {
    this.bundleGrid.innerHTML = '';

    for (let i = 0; i < this.packQuantity; i++) {
      const slot = this.createSlot(i);
      this.bundleGrid.appendChild(slot);
    }
  }

  /**
   * Create a single slot element
   */
  createSlot(index) {
    const slot = document.createElement('div');
    slot.className = 'build-box-bundle-slot';
    slot.dataset.slotIndex = index;
    slot.innerHTML = `
      <div class="build-box-slot-empty">
        <span class="build-box-slot-icon" aria-hidden="true">+</span>
      </div>
      <div class="build-box-slot-filled" style="display: none;">
        <div class="build-box-slot-image"></div>
        <div class="build-box-slot-name"></div>
        <ul class="build-box-slot-flavors" style="display: none;"></ul>
      </div>
    `;
    return slot;
  }

  /**
   * Update a slot with product data
   */
  updateSlot(index, product) {
    const slot = this.bundleGrid.querySelector(`[data-slot-index="${index}"]`);
    if (!slot) return;

    const emptyState = slot.querySelector('.build-box-slot-empty');
    const filledState = slot.querySelector('.build-box-slot-filled');
    const imageContainer = slot.querySelector('.build-box-slot-image');
    const nameElement = slot.querySelector('.build-box-slot-name');
    const flavorsElement = slot.querySelector('.build-box-slot-flavors');

    slot.dataset.productId = product.id;
    emptyState.style.display = 'none';
    filledState.style.display = 'block';
    imageContainer.innerHTML = `<img src="${product.image}" alt="${product.title}" class="build-box-slot-img">`;
    nameElement.textContent = product.title;

    // Add flavors if it's a Build Your Pint product
    if (product.isPint && product.selectedFlavors && product.selectedFlavors.length > 0) {
      flavorsElement.innerHTML = '';
      product.selectedFlavors.forEach(flavorId => {
        const li = document.createElement('li');
        li.textContent = flavorId;
        li.dataset.flavorId = flavorId;
        flavorsElement.appendChild(li);
      });
    }
  }

  /**
   * Regenerate all slots
   */
  regenerateSlots() {
    this.generateSlots();
    this.bundleProducts.forEach((product, index) => {
      this.updateSlot(index, product);
    });
  }

  /**
   * Update progress bar
   */
  updateProgress() {
    const currentCount = this.bundleProducts.length;
    const percentage = (currentCount / this.packQuantity) * 100;

    this.progressCurrent.textContent = currentCount;
    this.progressFill.style.width = `${percentage}%`;

    const remaining = this.packQuantity - currentCount;

    if (remaining === 0) {
      this.atcButton.disabled = false;
      this.atcButton.querySelector('.build-box-atc-text').innerHTML = 'ADD TO CART';
    } else {
      this.atcButton.disabled = true;
      this.atcButton.querySelector('.build-box-atc-text').innerHTML = `ADD <span class="build-box-atc-remaining">${remaining}</span> MORE`;
    }
  }

  /**
   * Update product quantity buttons
   */
  updateProductButtons(item) {
    const productId = item.dataset.productId;
    const decreaseBtn = item.querySelector('[data-quantity-decrease]');
    const increaseBtn = item.querySelector('[data-quantity-increase]');

    decreaseBtn.disabled = this.productQuantities[productId] === 0;
    increaseBtn.disabled = this.bundleProducts.length >= this.packQuantity;
  }

  /**
   * Update all product buttons
   */
  updateAllProductButtons() {
    this.productItems.forEach(item => this.updateProductButtons(item));
  }

  /**
   * Reset bundle to initial state
   */
  resetBundle() {
    this.bundleProducts = [];

    this.productItems.forEach(item => {
      const productId = item.dataset.productId;
      this.productQuantities[productId] = 0;
      item.querySelector('[data-quantity-value]').textContent = '0';
      this.updateProductButtons(item);
    });

    this.updateProgress();
  }

  /**
   * Setup purchase option handlers
   */
  setupPurchaseOptions() {
    this.purchaseOptions.forEach(option => {
      const button = option.querySelector('[data-purchase-type]');
      
      if (button) {
        button.addEventListener('click', () => {
          const purchaseType = button.dataset.purchaseType;
          this.selectPurchaseOption(option, purchaseType);
        });
      }
    });

    // Setup frequency selector
    if (this.frequencySelect) {
      this.frequencySelect.addEventListener('change', (e) => {
        this.selectedSellingPlanId = e.target.value;
        this.updateSubscriptionPrice();
        this.updateDiscountBadge();
      });

      // Set initial selling plan
      if (this.frequencySelect.options.length > 0) {
        this.selectedSellingPlanId = this.frequencySelect.options[0].value;
        this.updateSubscriptionPrice();
        this.updateDiscountBadge();
      }
    }
  }

  /**
   * Select purchase option (subscription or one-time)
   */
  selectPurchaseOption(option, purchaseType) {
    // Update UI
    this.purchaseOptions.forEach(opt => {
      opt.classList.remove('selected');
      const btn = opt.querySelector('[data-purchase-type]');
      if (btn) btn.setAttribute('aria-checked', 'false');
    });

    option.classList.add('selected');
    const button = option.querySelector('[data-purchase-type]');
    if (button) button.setAttribute('aria-checked', 'true');

    // Update state
    this.selectedPurchaseType = purchaseType;

    // Update subscription price if needed
    if (purchaseType === 'subscription') {
      this.updateSubscriptionPrice();
    }
  }

  /**
   * Update discount badge text
   */
  updateDiscountBadge() {
    if (!this.frequencySelect) return;

    const badgeDiscount = document.querySelector('[data-badge-discount]');
    const benefitDiscount = document.querySelector('[data-benefit-discount]');
    
    if (!badgeDiscount || !benefitDiscount) return;

    const selectedOption = this.frequencySelect.options[this.frequencySelect.selectedIndex];
    const discount = parseFloat(selectedOption.dataset.discount);
    const discountType = selectedOption.dataset.discountType;

    let badgeText = '';
    let benefitText = '';

    if (discountType === 'percentage') {
      badgeText = `${discount}% Off`;
      benefitText = `${discount}%`;
    } else {
      badgeText = `$${discount.toFixed(2)} Off`;
      benefitText = `$${discount.toFixed(2)}`;
    }

    badgeDiscount.textContent = badgeText;
    benefitDiscount.textContent = benefitText;
  }

  /**
   * Update subscription price display
   */
  updateSubscriptionPrice() {
    if (!this.frequencySelect) return;

    const selectedOption = this.frequencySelect.options[this.frequencySelect.selectedIndex];
    const discount = parseFloat(selectedOption.dataset.discount);
    const discountType = selectedOption.dataset.discountType;

    const selectedVariantItem = document.querySelector('[data-variant-item].selected');
    if (!selectedVariantItem) return;

    const variantPrice = parseFloat(selectedVariantItem.dataset.variantPrice);
    let subscriptionPrice;

    if (discountType === 'percentage') {
      subscriptionPrice = variantPrice - (variantPrice * discount / 100);
    } else {
      subscriptionPrice = variantPrice - discount;
    }

    const comparePriceElement = document.querySelector('[data-compare-price]');
    const subscriptionPriceElement = document.querySelector('[data-subscription-price]');

    if (comparePriceElement && subscriptionPriceElement) {
      comparePriceElement.textContent = this.formatMoney(variantPrice);
      subscriptionPriceElement.textContent = this.formatMoney(subscriptionPrice);
    }
  }

  /**
   * Update all price displays
   */
  updatePrices(variantItem) {
    const price = parseFloat(variantItem.dataset.variantPrice);
    const onetimePriceElement = document.querySelector('[data-onetime-price]');

    if (onetimePriceElement) {
      onetimePriceElement.textContent = this.formatMoney(price);
    }

    this.updateSubscriptionPrice();
  }

  /**
   * Format price as money
   */
  formatMoney(cents) {
    return '$' + cents.toFixed(2);
  }

  /**
   * Setup add to cart handler
   */
  setupAddToCart() {
    this.atcButton.addEventListener('click', () => this.addToCart());
  }

  /**
   * Add bundle to cart
   */
  async addToCart() {
    if (this.bundleProducts.length !== this.packQuantity) return;

    // Create product list with quantities and flavors
    const productCounts = {};
    const pintProducts = [];

    this.bundleProducts.forEach(product => {
      if (product.isPint && product.selectedFlavors) {
        // Store Build Your Pint products separately
        pintProducts.push({
          title: product.title,
          flavors: product.selectedFlavors
        });
      } else {
        // Count regular products
        const title = product.title;
        productCounts[title] = (productCounts[title] || 0) + 1;
      }
    });

    // Build product list string with line breaks
    let productListParts = [];

    // Add regular products (each on new line)
    Object.entries(productCounts).forEach(([title, count]) => {
      productListParts.push(count > 1 ? `(${count}x) ${title}` : title);
    });

    // Add Build Your Pint products with numbered flavors
    pintProducts.forEach(pint => {
      // Create numbered list: 1. Ice Cream, 2. Banana, etc.
      const numberedFlavors = pint.flavors.map((flavor, index) => {
        return `${index + 1}. ${flavor}`;
      }).join(', ');
      
      productListParts.push(`${pint.title}: ${numberedFlavors}`);
    });

    // Join with line breaks for better readability in cart
    const productList = productListParts.join('\n');

    // Get subscription plan name if applicable
    let subscriptionPlanName = '';
    if (this.selectedPurchaseType === 'subscription' && this.frequencySelect) {
      const selectedOption = this.frequencySelect.options[this.frequencySelect.selectedIndex];
      subscriptionPlanName = selectedOption.text;
    }

    // Build cart item
    const cartItem = {
      id: this.selectedVariant,
      quantity: 1,
      properties: {
        'Build Box': productList
      }
    };

    // Add subscription property if applicable
    if (this.selectedPurchaseType === 'subscription' && subscriptionPlanName) {
      cartItem.properties['Subscription'] = subscriptionPlanName;
    }

    // Add selling plan if subscription
    if (this.selectedPurchaseType === 'subscription' && this.selectedSellingPlanId) {
      cartItem.selling_plan = this.selectedSellingPlanId;
    }

    const requestData = {
      items: [cartItem]
    };

    try {
      // Add to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      const result = await response.json();
      console.log('Added to cart:', result);

      // Update cart drawer or redirect
      const cartDrawer = document.querySelector('m-cart-drawer');
      
      if (cartDrawer) {
        // Update cart drawer
        fetch(`${window.Shopify.routes.root}cart?section_id=cart-drawer`)
          .then(res => res.text())
          .then(html => {
            cartDrawer.renderContents({
              id: result.id,
              sections: {
                'cart-drawer': html
              }
            });
          });
      } else {
        // Redirect to cart page
        window.location.href = '/cart';
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Error adding to cart. Please try again.');
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new BuildYourBox();
});
